import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Send, Mail, User, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useSite } from '../contexts/SiteContext';
import { SEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ContactPage() {
  const { settings } = useSite();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    honeypot: '',
    math_answer: ''
  });
  const [challenge, setChallenge] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchChallenge();
  }, []);

  const fetchChallenge = async () => {
    try {
      const response = await axios.get(`${API}/contact/challenge`);
      setChallenge(response.data);
    } catch (error) {
      console.error('Error fetching challenge:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.math_answer) {
      toast.error('Please answer the security question');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        math_answer: parseInt(formData.math_answer, 10),
        challenge_id: challenge?.id || ''
      });
      
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        message: '',
        honeypot: '',
        math_answer: ''
      });
      fetchChallenge(); // Get new challenge
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="contact-page">
      <SEO
        title="Contact"
        description="Have a question or want to work together? Get in touch."
        url="/contact"
      />
      <div className="max-w-xl mx-auto">
        <header className="text-center mb-12 animate-fade-in">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter mb-4">
            Get in Touch
          </h1>
          <p className="text-muted-foreground">
            Have a question or want to work together? Send me a message!
          </p>
        </header>

        <form 
          onSubmit={handleSubmit} 
          className="space-y-6 animate-fade-in-delay-1"
          data-testid="contact-form"
        >
          {/* Honeypot - hidden from users, visible to bots */}
          <div className="sr-only" aria-hidden="true">
            <Label htmlFor="honeypot">Leave this empty</Label>
            <Input
              type="text"
              id="honeypot"
              name="honeypot"
              value={formData.honeypot}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Your name"
              data-testid="contact-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your@email.com"
              data-testid="contact-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Message <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              placeholder="Your message..."
              rows={5}
              data-testid="contact-message-input"
            />
          </div>

          {/* Math Challenge - Bot Protection */}
          {challenge && (
            <div className="space-y-2 p-4 bg-muted rounded-lg">
              <Label htmlFor="math_answer" className="text-sm">
                Security Question: {challenge.question} <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                id="math_answer"
                name="math_answer"
                value={formData.math_answer}
                onChange={handleChange}
                required
                placeholder="Your answer"
                className="max-w-[120px]"
                data-testid="contact-math-input"
              />
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full" 
            disabled={submitting}
            data-testid="contact-submit-btn"
          >
            {submitting ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Sending...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send Message
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
